import { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "components/ui/popover";
import { useGetRegionalsQuery } from "@/services/regional";
import { IRegional } from "@/@types/regional";
import { useDebouncedValue } from "@/hooks/use-debounced-value";

interface RegionalComboboxProps {
  value?: number;
  onSelect: (id: number) => void;
  disabled?: boolean;
}

const ORDER_BY_NAME = JSON.stringify({ name: "asc" });

export function RegionalCombobox({
  value,
  onSelect,
  disabled = false,
}: RegionalComboboxProps) {
  const [open, setOpen] = useState<boolean>(false);
  const [search, setSearch] = useState<string>("");
  const debouncedSearch = useDebouncedValue(search, 300);

  const { data, isFetching } = useGetRegionalsQuery(
    {
      search: debouncedSearch,
      orderBy: ORDER_BY_NAME,
      meta: { page: 1 },
      perPage: 50,
    },
    { skip: disabled },
  );

  const regionals: IRegional[] = data?.data ?? [];
  const selected = regionals.find((regional) => regional.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label="Selecionar regional"
          className={cn(
            "w-full justify-between",
            !selected && "text-muted-foreground",
          )}
          disabled={disabled}
        >
          {selected ? selected.name : "Selecione o regional..."}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] max-w-[420px] p-0"
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Buscar regional..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {isFetching ? (
              <div className="px-4 py-2 text-sm text-muted-foreground">
                Carregando regionais...
              </div>
            ) : (
              <>
                <CommandEmpty>Nenhum regional encontrado.</CommandEmpty>
                <CommandGroup>
                  {regionals.map((regional) => (
                    <CommandItem
                      key={regional.id}
                      value={`${regional.id}`}
                      onSelect={() => {
                        onSelect(regional.id);
                        setOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          value === regional.id ? "opacity-100" : "opacity-0",
                        )}
                      />
                      <span className="flex flex-col">
                        <span className="font-medium">{regional.name}</span>
                        {regional.email && (
                          <span className="text-xs text-muted-foreground">
                            {regional.email}
                          </span>
                        )}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
